import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../i18n';
import { useAuth } from '../../contexts/AuthContext';
import { useOffline } from '../../contexts/OfflineContext';
import { usePosSubtitle } from '../PosLayout';
import {
  getDevices,
  getPosSettingsPayload,
  printPosNullbeleg,
  resyncPosFiscalOrders,
  savePosSettingsConfig,
  testPosPrinter,
  updatePosPrintGroup,
  uploadPosSupportLog,
  waitForPrintJob,
} from '../../lib/api';
import { printerTypeLabel } from '../../lib/printTransports';
import DeviceSettingsPanel from '../DeviceSettingsPanel';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Cloud,
  FileText,
  Globe,
  HelpCircle,
  Info,
  Loader2,
  LogOut,
  MonitorSmartphone,
  Palette,
  Printer,
  RefreshCw,
  ServerCog,
  Smartphone,
  Upload,
  Wifi,
  WifiOff,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  appendSupportLogEntry,
  applyPosUiPreferences,
  clearSupportLogEntries,
  downloadJsonFile,
  readPosUiPreferences,
  readSupportLogEntries,
  writePosUiPreferences,
} from '../settingsRuntime';
import { DetailCard, KeyValueRows, SectionHeader, SelectField, StatusBadge } from '../settingsUi';

const MENU_ITEMS = [
  { id: 'fiskalisierung', label: 'Fiskalisierung', icon: Cloud, kind: 'section' },
  { id: 'geraet', label: 'Dieses Gerät', icon: Smartphone, kind: 'section' },
  { id: 'druckerzuweisung', label: 'Druckerzuweisung', icon: Printer, kind: 'section' },
  { id: 'druckerstatus', label: 'Druckerstatus', icon: Printer, kind: 'section' },
  { id: 'nebenterminals', label: 'Nebenterminals', icon: MonitorSmartphone, kind: 'section' },
  { id: 'dienste', label: 'Dienste', icon: ServerCog, kind: 'section' },
  { id: 'sprache', label: 'Sprache', icon: Globe, kind: 'section' },
  { id: 'darstellung', label: 'Darstellung', icon: Palette, kind: 'section' },
  { id: 'demo', label: 'Demo aktivieren (Sync deaktivieren)', icon: BookOpen, kind: 'section' },
  { id: 'sync', label: 'Jetzt synchronisieren', icon: RefreshCw, kind: 'action' },
  { id: 'hilfe', label: 'Hilfe', icon: HelpCircle, kind: 'section' },
  { id: 'support_log', label: 'Support Log', icon: FileText, kind: 'section' },
  { id: 'support_log_upload', label: 'Support Log hochladen', icon: Upload, kind: 'action' },
  { id: 'restart', label: 'Anwendung neu starten', icon: AlertTriangle, kind: 'action', tone: 'warning' },
  { id: 'logout', label: 'Abmelden', icon: LogOut, kind: 'action', tone: 'danger' },
];

const SYSTEM_GROUPS = [
  { id: 'invoice', label: 'Rechnungsdrucker', usedAs: ['Rechnung'] },
  { id: 'report', label: 'Berichtsdrucker', usedAs: ['Bericht'] },
  { id: 'receipt', label: 'Bondrucker', usedAs: ['Bon', 'Theke'] },
  { id: 'announce', label: 'Annoncierdrucker', usedAs: ['Küche'] },
];

const SECTION_SUBTITLES = {
  fiskalisierung: 'Fiskalisierung',
  geraet: 'Dieses Gerät',
  druckerzuweisung: 'Druckerzuweisung',
  druckerstatus: 'Druckerstatus',
  nebenterminals: 'Nebenterminals',
  dienste: 'Dienste',
  sprache: 'Sprache',
  darstellung: 'Darstellung',
  demo: 'Demo-Modus',
  hilfe: 'Hilfe',
  support_log: 'Support Log',
};

const DEFAULT_POS_SETTINGS_CONFIG = {
  system_printers: {
    invoice: null,
    report: null,
    receipt: null,
    announce: null,
  },
  room_printers: {},
};

const LANGUAGE_OPTIONS = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
  { value: 'sq', label: 'Shqip' },
];

const UI_OPTION_GROUPS = [
  {
    key: 'density',
    label: 'Dichte',
    description: 'Wie kompakt Oberflächen und Listen im POS aufgebaut sind.',
    options: [
      { value: 'compact', label: 'Kompakt' },
      { value: 'comfortable', label: 'Komfort' },
    ],
  },
  {
    key: 'contrast',
    label: 'Kontrast',
    description: 'Mehr Lesbarkeit auf hellen Kassen-Displays und Tablets.',
    options: [
      { value: 'normal', label: 'Standard' },
      { value: 'high', label: 'Erhöht' },
    ],
  },
  {
    key: 'motion',
    label: 'Animationen',
    description: 'Reduziert Bewegungen bei Animationen und Übergängen.',
    options: [
      { value: 'reduced', label: 'Reduziert' },
      { value: 'normal', label: 'Standard' },
    ],
  },
  {
    key: 'touchMode',
    label: 'Touch-Ziele',
    description: 'Größe von Buttons und Eingaben für Touch-Bedienung.',
    options: [
      { value: 'comfortable', label: 'Komfort' },
      { value: 'spacious', label: 'Groß' },
    ],
  },
  {
    key: 'theme',
    label: 'Farbschema',
    description: 'Helles oder dunkles Design für die Kassenoberfläche.',
    options: [
      { value: 'light', label: 'Hell' },
      { value: 'dark', label: 'Dunkel' },
    ],
  },
];
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

function normalizePosSettingsConfig(rawValue) {
  const raw = rawValue && typeof rawValue === 'object' ? rawValue : {};
  const rawSystemPrinters = raw.system_printers && typeof raw.system_printers === 'object'
    ? raw.system_printers
    : {};
  const rawRoomPrinters = raw.room_printers && typeof raw.room_printers === 'object'
    ? raw.room_printers
    : {};

  return {
    system_printers: {
      invoice: rawSystemPrinters.invoice || null,
      report: rawSystemPrinters.report || null,
      receipt: rawSystemPrinters.receipt || null,
      announce: rawSystemPrinters.announce || null,
    },
    room_printers: Object.fromEntries(
      Object.entries(rawRoomPrinters).map(([roomId, printerId]) => [roomId, printerId || null]),
    ),
  };
}

function formatDateTime(value) {
  if (!value) return 'Noch keine Aktivität';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unbekannt';
  return new Intl.DateTimeFormat('de-AT', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

function formatRegisterState(register) {
  const status = String(register?.status || '').toLowerCase();
  if (status === 'open') return 'Schicht offen';
  if (status === 'closed') return 'Schicht geschlossen';
  if (status === 'review_required') return 'Prüfung erforderlich';
  if (register?.opened_at) return 'Schicht gestartet';
  return 'Noch nicht geöffnet';
}

function getFiscalSummary(fiscalStatus, settings) {
  if (fiscalStatus?.tse_ok) {
    return {
      tone: 'positive',
      label: 'fiskaltrust aktiv',
      description: 'Die Kassa signiert Belege mit aktiver fiskaltrust-Anbindung.',
    };
  }

  if (fiscalStatus?.mock_mode || settings?.ft_is_mock) {
    return {
      tone: 'warning',
      label: 'Mock-Modus',
      description: 'Die Fiskalisierung läuft im Testmodus und erzeugt Testbelege.',
    };
  }

  return {
    tone: 'danger',
    label: 'Konfiguration prüfen',
    description: 'Die Fiskalisierung ist noch nicht vollständig konfiguriert.',
  };
}

function AssignmentRow({
  title,
  subtitle,
  badges = [],
  options,
  value,
  onChange,
  disabled = false,
  tone = 'neutral',
}) {
  return (
    <div className="grid gap-4 border-b border-border px-4 py-4 last:border-b-0 md:grid-cols-[minmax(0,1fr)_280px]">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-base font-semibold text-foreground">{title}</p>
          {badges.map((badge) => (
            <span key={badge} className="bg-slate-900 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white">
              {badge}
            </span>
          ))}
        </div>
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <SelectField value={value} options={options} onChange={onChange} disabled={disabled} />
        </div>
        <StatusBadge tone={tone}>{disabled ? 'Speichert' : value ? 'Aktiv' : 'Standard'}</StatusBadge>
      </div>
    </div>
  );
}

function FiskalisierungPanel({
  companyName,
  settings,
  fiscalStatus,
  statusItems,
  cashRegister,
  onRefresh,
  onResync,
  isRefreshing,
  isResyncing,
}) {
  const positiveCount = statusItems.filter((item) => item.ok).length;
  const fiscalSummary = getFiscalSummary(fiscalStatus, settings);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Fiskalisierung"
        subtitle="Status der Hauptkassa, fiskaltrust-Anbindung und Signaturbereitschaft."
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRefreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Aktualisieren
            </button>
            <button
              type="button"
              onClick={onResync}
              disabled={isResyncing}
              className="inline-flex h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isResyncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Offene Belege prüfen
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <DetailCard title="Kassenstatus" subtitle={companyName}>
            <div className="flex items-center gap-4">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-lime-100">
                <Cloud className="h-12 w-12 text-lime-600" />
              </div>
              <div>
                <p className="text-4xl font-black text-foreground">{positiveCount}/{statusItems.length}</p>
                <p className="text-sm text-muted-foreground">Checks erfolgreich</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusBadge tone={positiveCount === statusItems.length ? 'positive' : 'warning'}>
                    {positiveCount === statusItems.length ? 'Alles OK' : 'Prüfung offen'}
                  </StatusBadge>
                  <StatusBadge tone={fiscalSummary.tone}>{fiscalSummary.label}</StatusBadge>
                </div>
              </div>
            </div>

            <div className="mt-6 border-t border-border pt-4">
              <KeyValueRows
                rows={[
                  { label: 'Terminal', value: settings?.terminal_id || 'POS_001' },
                  { label: 'Kassen-ID', value: settings?.kassen_id || 'KASSE001' },
                  { label: 'Letzte Signatur', value: formatDateTime(fiscalStatus?.last_signature_at) },
                  { label: 'Schichtstatus', value: formatRegisterState(cashRegister) },
                ]}
              />
            </div>
          </DetailCard>

          <div className="space-y-5">
            <DetailCard title="Verbindungs- und Betriebsstatus" subtitle="Die Prüfungen basieren auf echten POS- und Fiskaldaten dieser Sitzung.">
              <div className="grid gap-4 md:grid-cols-2">
                {statusItems.map((item) => (
                  <div key={item.label} className="border border-border px-4 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {item.ok ? (
                          <CheckCircle2 className="h-5 w-5 text-lime-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-amber-600" />
                        )}
                        <p className="font-semibold text-foreground">{item.label}</p>
                      </div>
                      <StatusBadge tone={item.ok ? 'positive' : item.tone || 'warning'}>
                        {item.ok ? 'OK' : item.badge || 'Prüfen'}
                      </StatusBadge>
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">{item.value}</p>
                  </div>
                ))}
              </div>
            </DetailCard>

            <DetailCard title="Fiskalzustand" subtitle={fiscalSummary.description}>
              <KeyValueRows
                rows={[
                  { label: 'fiskaltrust', value: fiscalStatus?.tse_ok ? 'Produktiv aktiv' : fiscalStatus?.mock_mode ? 'Mock / Testmodus' : 'Nicht bereit' },
                  { label: 'CashBox', value: fiscalStatus?.cashbox_configured ? 'Konfiguriert' : 'Fehlt oder unvollständig' },
                  { label: 'Fehler seit', value: fiscalStatus?.failure_since ? formatDateTime(fiscalStatus.failure_since) : 'Kein aktiver Fehler gemeldet' },
                  { label: 'Belegtyp unten', value: 'Null-Beleg wird direkt an fiskaltrust gesendet' },
                ]}
              />
            </DetailCard>
          </div>
        </div>
      </div>
    </div>
  );
}

function DruckerzuweisungPanel({
  printerOptions: labelledPrinterOptions,
  printGroups,
  rooms,
  systemAssignments,
  roomAssignments,
  savingKey,
  onAssignSystemPrinter,
  onAssignGroupPrinter,
  onAssignRoomPrinter,
}) {
  const printerOptions = useMemo(
    () => [{ value: '', label: 'Nicht drucken' }, ...labelledPrinterOptions],
    [labelledPrinterOptions],
  );
  const roomOptions = useMemo(
    () => [{ value: '', label: 'Standardrouting verwenden' }, ...labelledPrinterOptions],
    [labelledPrinterOptions],
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Druckerzuweisung"
        subtitle="Firmen-Standard: welcher Drucker (an welchem Gerät) welche Ausgabe druckt. Einzelne Geräte können Bon/Rechnung unter „Dieses Gerät“ selbst überschreiben."
      />

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <DetailCard
          title="Systemgruppen"
          subtitle="Diese Zuweisungen werden im laufenden POS direkt verwendet und im Unternehmen gespeichert."
        >
          <div className="divide-y divide-slate-100 border border-border bg-card">
            {SYSTEM_GROUPS.map((group) => (
              <AssignmentRow
                key={group.id}
                title={group.label}
                subtitle={systemAssignments[group.id] ? 'Drucker fest zugewiesen' : 'Fallback auf Standarddrucker oder kein Ausdruck'}
                badges={group.usedAs}
                options={printerOptions}
                value={systemAssignments[group.id] || ''}
                onChange={(value) => onAssignSystemPrinter(group.id, value || null)}
                disabled={savingKey === `system:${group.id}`}
                tone={systemAssignments[group.id] ? 'positive' : 'neutral'}
              />
            ))}
          </div>
        </DetailCard>

        <DetailCard title="Druckgruppen" subtitle="Artikelgruppen und Annoncierlogik pro Drucker steuern.">
          <div className="divide-y divide-slate-100 border border-border bg-card">
            {printGroups.length > 0 ? (
              printGroups.map((group) => (
                <AssignmentRow
                  key={group.id}
                  title={group.name}
                  subtitle={group.printer_id ? 'Eigene Druckgruppe mit direkter Zuweisung' : 'Noch ohne eigenen Drucker'}
                  options={printerOptions}
                  value={group.printer_id || ''}
                  onChange={(value) => onAssignGroupPrinter(group.id, value || null)}
                  disabled={savingKey === `group:${group.id}`}
                  tone={group.printer_id ? 'positive' : 'neutral'}
                />
              ))
            ) : (
              <div className="flex items-start gap-3 px-4 py-4 text-sm text-muted-foreground">
                <Info className="mt-0.5 h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-semibold text-muted-foreground">Keine Druckgruppen vorhanden</p>
                  <p className="mt-1">Sobald im Adminbereich Druckgruppen angelegt werden, kannst du sie hier einem Drucker zuweisen.</p>
                </div>
              </div>
            )}
          </div>
        </DetailCard>

        <DetailCard title="Raumspezifisch anpassen" subtitle="Optional einzelne Räume auf einen anderen Drucker routen.">
          <div className="divide-y divide-slate-100 border border-border bg-card">
            {rooms.length > 0 ? (
              rooms.map((room) => (
                <AssignmentRow
                  key={room.id}
                  title={room.name}
                  subtitle={roomAssignments[room.id] ? 'Eigener Raumdrucker aktiv' : 'Standardrouting aktiv'}
                  options={roomOptions}
                  value={roomAssignments[room.id] || ''}
                  onChange={(value) => onAssignRoomPrinter(room.id, value || null)}
                  disabled={savingKey === `room:${room.id}`}
                  tone={roomAssignments[room.id] ? 'positive' : 'neutral'}
                />
              ))
            ) : (
              <div className="flex items-start gap-3 px-4 py-4 text-sm text-muted-foreground">
                <Info className="mt-0.5 h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-semibold text-muted-foreground">Keine Räume konfiguriert</p>
                  <p className="mt-1">Lege zuerst Gasträume oder Küchenbereiche an, damit hier raumspezifische Druckregeln erscheinen.</p>
                </div>
              </div>
            )}
          </div>
        </DetailCard>
      </div>
    </div>
  );
}

function DruckerstatusPanel({
  printers,
  devicesById,
  printGroups,
  selectedPrinterId,
  onSelectPrinter,
  onRefresh,
  onTestPrinter,
  isRefreshing,
  testingPrinterId,
  lastDeviceRefreshAt,
}) {
  const selectedPrinter = printers.find((printer) => printer.id === selectedPrinterId) || printers[0] || null;
  const assignedGroups = printGroups.filter((group) => group.printer_id === selectedPrinter?.id);
  const selectedDevice = selectedPrinter?.device_id ? devicesById[selectedPrinter.device_id] : null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Druckerstatus"
        subtitle="Verfügbarkeit, Verbindung und Rollen pro Drucker auf einen Blick."
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRefreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Aktualisieren
            </button>
            <button
              type="button"
              onClick={() => selectedPrinter && onTestPrinter(selectedPrinter)}
              disabled={!selectedPrinter || testingPrinterId === selectedPrinter?.id}
              className="inline-flex h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {testingPrinterId === selectedPrinter?.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
              Testseite
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-5">
        {printers.length === 0 ? (
          <DetailCard title="Keine Drucker verbunden" subtitle="Drucker werden am Gerät eingerichtet, an dem sie hängen.">
            <p className="text-sm text-muted-foreground">
              Unter „Dieses Gerät“ das Gerät registrieren und seine Drucker hinzufügen.
            </p>
          </DetailCard>
        ) : (
          <div className="grid gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
            <div className="space-y-3">
              {printers.map((printer) => {
                const isActive = selectedPrinter?.id === printer.id;
                const printerDevice = printer.device_id ? devicesById[printer.device_id] : null;
                return (
                  <button
                    key={printer.id}
                    type="button"
                    onClick={() => onSelectPrinter(printer.id)}
                    className={`w-full border px-4 py-4 text-left transition-colors ${
                      isActive ? 'border-slate-900 bg-muted' : 'border-border bg-card hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-lg font-bold text-foreground">{printer.name}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{printer.connection_string || 'USB / Netzwerk'}</p>
                      </div>
                      <StatusBadge tone={!printer.device_id ? 'danger' : printerDevice?.online ? 'positive' : 'warning'}>
                        {!printer.device_id ? 'Kein Gerät' : printerDevice?.online ? 'Gerät online' : 'Gerät offline'}
                      </StatusBadge>
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-xs uppercase tracking-[0.08em] text-muted-foreground">
                      <span>{printerTypeLabel(printer.type)}</span>
                      <span>{printerDevice?.name || 'Ohne Gerät'}</span>
                      <span>{printer.paper_width} mm</span>
                      {printer.is_default ? <span>Standard</span> : null}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="space-y-5">
              <DetailCard title={selectedPrinter?.name || 'Druckerdetails'} subtitle={selectedPrinter?.connection_string || 'USB / Netzwerk'}>
                <KeyValueRows
                  rows={[
                    { label: 'Name', value: selectedPrinter?.name || '—' },
                    { label: 'Gerät', value: selectedDevice ? `${selectedDevice.name} (${selectedDevice.online ? 'online' : 'offline'})` : 'Keinem Gerät zugeordnet' },
                    { label: 'Typ', value: printerTypeLabel(selectedPrinter?.type) },
                    { label: 'Verbindung', value: selectedPrinter?.connection_string || 'Nicht gesetzt' },
                    { label: 'Papierbreite', value: selectedPrinter ? `${selectedPrinter.paper_width} mm` : '—' },
                    { label: 'Standarddrucker', value: selectedPrinter?.is_default ? 'Ja' : 'Nein' },
                    { label: 'Letzte Aktualisierung', value: formatDateTime(lastDeviceRefreshAt) },
                  ]}
                />
              </DetailCard>

              <DetailCard title="Zuweisungen" subtitle="Welche Bereiche aktuell über diesen Drucker laufen.">
                <div className="flex flex-wrap gap-2">
                  {assignedGroups.length > 0 ? (
                    assignedGroups.map((group) => (
                      <span
                        key={group.id}
                        className="bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                      >
                        {group.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">Aktuell keine direkte Druckgruppe auf diesem Gerät.</span>
                  )}
                </div>
              </DetailCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NebenterminalsPanel({ deviceInfo, devices, settings, cashRegister, lastSyncTime, pendingOrdersCount, demoModeEnabled }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Nebenterminals"
        subtitle="Status der Hauptkassa, dieses Geräts und der laufenden Sitzung."
      />

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <DetailCard title={settings?.kassen_id || 'Hauptkassa'} subtitle="Aktive Kasseninstanz dieses Unternehmens">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <p className="text-lg font-bold text-foreground">{formatRegisterState(cashRegister)}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {cashRegister?.opened_at ? `Gestartet am ${formatDateTime(cashRegister.opened_at)}` : 'Noch keine offene Schicht im Tagesregister gefunden.'}
              </p>
            </div>
            <StatusBadge tone={cashRegister?.status === 'open' ? 'positive' : 'warning'}>
              {cashRegister?.status === 'open' ? 'Online' : 'Prüfen'}
            </StatusBadge>
          </div>

          <KeyValueRows
            rows={[
              { label: 'Terminal-ID', value: settings?.terminal_id || 'POS_001' },
              { label: 'Kassen-ID', value: settings?.kassen_id || 'KASSE001' },
              { label: 'Letzte Synchronisation', value: formatDateTime(lastSyncTime) },
              { label: 'Lokale Warteschlange', value: `${pendingOrdersCount} Vorgänge` },
              { label: 'Demo-Modus', value: demoModeEnabled ? 'Aktiv' : 'Aus' },
            ]}
          />
        </DetailCard>

        <DetailCard title="Registrierte Geräte" subtitle="Alle Kassen, PCs und Handys dieser Firma mit ihren Druckern.">
          {devices.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {devices.map((device) => (
                <div key={device.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{device.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {device.platform || 'Unbekannt'} · {device.printers?.length || 0} Drucker · zuletzt {formatDateTime(device.last_seen_at)}
                    </p>
                  </div>
                  <StatusBadge tone={device.online ? 'positive' : 'warning'}>{device.online ? 'Online' : 'Offline'}</StatusBadge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Noch keine Geräte registriert. Unter „Dieses Gerät“ registrieren.</p>
          )}
        </DetailCard>

        <DetailCard title="Lokales Gerät" subtitle="Echtzeitdaten aus Browser, PWA und Session">
          <KeyValueRows
            rows={[
              { label: 'App-Version', value: 'FiscalPOS PWA' },
              { label: 'PWA Modus', value: deviceInfo.isStandalone ? 'Standalone' : 'Browser-Tab' },
              { label: 'Service Worker', value: deviceInfo.serviceWorkerSupported ? 'Verfügbar' : 'Nicht verfügbar' },
              { label: 'IP / Host', value: deviceInfo.ipAddress },
              { label: 'Gerät', value: `${deviceInfo.vendor} · ${deviceInfo.model}` },
              { label: 'Betriebssystem', value: `${deviceInfo.os} · ${deviceInfo.platformVersion}` },
            ]}
          />
        </DetailCard>
      </div>
    </div>
  );
}

function DienstePanel({ statusItems, serviceItems, companyName }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader
        title="Dienste"
        subtitle="Koordiniert lokale Prozesse, Cloud-Sync, Druck und Diagnose."
      />

      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
          <DetailCard title={companyName} subtitle="Aktive POS-Sitzung">
            <div className="space-y-4">
              {statusItems.map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-4 border-b border-border pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className={`h-5 w-5 ${item.ok ? 'text-lime-600' : 'text-amber-600'}`} />
                    <span className="text-base text-muted-foreground">{item.label}</span>
                  </div>
                  <StatusBadge tone={item.ok ? 'positive' : item.tone || 'warning'}>
                    {item.ok ? 'Aktiv' : item.badge || 'Prüfen'}
                  </StatusBadge>
                </div>
              ))}
            </div>
          </DetailCard>

          <DetailCard title="Diensteverwaltung" subtitle="Die Liste spiegelt die aktuell nutzbaren POS-Dienste in dieser Session wider.">
            <div className="divide-y divide-slate-100 border border-border bg-card">
              {serviceItems.map((service) => (
                <div key={service.label} className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 px-4 py-4">
                  <div>
                    <p className="text-xl font-bold text-foreground">{service.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge tone={service.active ? 'positive' : service.tone || 'warning'}>
                      {service.active ? 'Aktiv' : 'Pause'}
                    </StatusBadge>
                  </div>
                </div>
              ))}
            </div>
          </DetailCard>
        </div>
      </div>
    </div>
  );
}

function SprachePanel({ language, settings, onChangeLanguage }) {
  const currentDate = useMemo(() => new Date(), []);
  const locale = language === 'en' ? 'en-US' : language === 'sq' ? 'sq-AL' : 'de-AT';

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader title="Sprache" subtitle="Grundsprache und Ausgabeformate der Kassa." />
      <div className="flex-1 overflow-y-auto p-5 grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
        <DetailCard title="Aktive Sprache" subtitle="Die Änderung wirkt direkt auf diese laufende Sitzung.">
          <div className="space-y-4">
            <SelectField
              value={language}
              options={LANGUAGE_OPTIONS}
              onChange={onChangeLanguage}
            />
            <p className="text-sm text-muted-foreground">
              Die gewählte Sprache wird lokal auf diesem Gerät gespeichert und beim nächsten Start wiederverwendet.
            </p>
          </div>
        </DetailCard>

        <DetailCard title="Formatvorschau" subtitle="So werden Beträge und Zeitangaben aktuell dargestellt.">
          <KeyValueRows
            rows={[
              { label: 'Oberfläche', value: LANGUAGE_OPTIONS.find((option) => option.value === language)?.label || 'Deutsch' },
              { label: 'Währung', value: `${settings?.currency || 'EUR'} (${settings?.currency_symbol || '€'})` },
              { label: 'Datum', value: new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(currentDate) },
              { label: 'Zeit', value: new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(currentDate) },
              { label: 'Betragsformat', value: new Intl.NumberFormat(locale, { style: 'currency', currency: settings?.currency || 'EUR' }).format(1234.56) },
            ]}
          />
        </DetailCard>
      </div>
    </div>
  );
}

function DarstellungPanel({ uiPreferences, onUpdatePreference }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader title="Darstellung" subtitle="Anzeigeprofil für Kasse, Tablet und Mobile-PWA." />
      <div className="flex-1 overflow-y-auto p-5 grid gap-5 xl:grid-cols-2">
        {UI_OPTION_GROUPS.map((group) => (
          <DetailCard key={group.key} title={group.label} subtitle={group.description}>
            <SelectField
              value={uiPreferences[group.key]}
              options={group.options}
              onChange={(value) => onUpdatePreference(group.key, value)}
            />
          </DetailCard>
        ))}
      </div>
    </div>
  );
}

function DemoPanel({ demoEnabled, onToggle, pendingOrdersCount }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader title="Demo-Modus" subtitle="Sync deaktivieren und lokal ohne Übertragung testen." />
      <div className="flex-1 overflow-y-auto p-5">
        <div className="mx-auto max-w-2xl border border-border bg-card p-8 text-center">
          <BookOpen className="mx-auto h-16 w-16 text-slate-300" />
          <h3 className="mt-5 text-2xl font-bold text-foreground">Demo aktivieren</h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Im Demo-Modus bleibt die Kassa voll bedienbar, sendet aber keine echten Daten an die Cloud.
            Offene Vorgänge bleiben lokal auf diesem Gerät erhalten.
          </p>

          <div className="mt-5 grid gap-3 text-left md:grid-cols-2">
            <div className="border border-border p-4">
              <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Status</p>
              <p className="mt-2 text-lg font-bold text-foreground">{demoEnabled ? 'Aktiv' : 'Inaktiv'}</p>
            </div>
            <div className="border border-border p-4">
              <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Lokale Warteschlange</p>
              <p className="mt-2 text-lg font-bold text-foreground">{pendingOrdersCount} Vorgänge</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onToggle}
            className={`mt-6 inline-flex h-12 items-center justify-center px-6 text-sm font-semibold transition-colors ${
              demoEnabled
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'border border-border bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            {demoEnabled ? 'Demo-Modus deaktivieren' : 'Demo-Modus aktivieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HilfePanel({ onSyncNow, onOpenSupportLog, onOpenPrinterStatus, onDownloadSupportLog, isSyncing }) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader title="Hilfe" subtitle="Schnelle Hilfe für Betrieb, Support und Druck." />
      <div className="flex-1 overflow-y-auto p-5 grid gap-5 xl:grid-cols-2">
        <DetailCard title="Soforthilfe">
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>1. Jetzt synchronisieren, wenn Online-Bestellungen oder lokale Vorgänge hängen bleiben.</p>
            <p>2. Druckerstatus öffnen und Testseite senden, wenn Bons oder Rechnungen fehlen.</p>
            <p>3. Support-Log herunterladen oder hochladen, wenn ein Problem dokumentiert werden muss.</p>
          </div>
        </DetailCard>

        <DetailCard title="Direkte Aktionen">
          <div className="space-y-3">
            <button
              type="button"
              onClick={onSyncNow}
              className="flex h-11 w-full items-center justify-center border border-border bg-card text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              {isSyncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Jetzt synchronisieren
            </button>
            <button
              type="button"
              onClick={onOpenPrinterStatus}
              className="flex h-11 w-full items-center justify-center border border-border bg-card text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              <Printer className="mr-2 h-4 w-4" />
              Druckerstatus öffnen
            </button>
            <button
              type="button"
              onClick={onDownloadSupportLog}
              className="flex h-11 w-full items-center justify-center border border-border bg-card text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              <FileText className="mr-2 h-4 w-4" />
              Support-Log herunterladen
            </button>
            <button
              type="button"
              onClick={onOpenSupportLog}
              className="flex h-11 w-full items-center justify-center bg-lime-500 text-sm font-semibold text-white transition-colors hover:bg-lime-600"
            >
              Support-Log anzeigen
            </button>
          </div>
        </DetailCard>
      </div>
    </div>
  );
}

function SupportLogPanel({
  entries,
  snapshot,
  isUploading,
  onDownload,
  onUpload,
  onClear,
}) {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SectionHeader title="Support Log" subtitle="Diagnose, lokale Ereignisse und Upload für den Support." />
      <div className="flex-1 overflow-y-auto p-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <DetailCard title="Aktueller Zustand">
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="border border-border bg-muted p-4 font-mono text-xs text-muted-foreground">
              session: {snapshot.session_id}
              <br />
              generated: {formatDateTime(snapshot.generated_at)}
              <br />
              user: {snapshot.user?.name || 'Unbekannt'}
              <br />
              printer: {snapshot.printers?.length || 0}
              <br />
              pending: {snapshot.pending_orders_count || 0}
            </div>
            <div className="divide-y divide-slate-100 border border-border">
              {entries.length > 0 ? entries.slice(0, 14).map((entry) => (
                <div key={entry.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground">{entry.message || entry.type || 'Logeintrag'}</p>
                    <StatusBadge tone={entry.level === 'error' ? 'danger' : entry.level === 'warning' ? 'warning' : 'neutral'}>
                      {entry.level || 'info'}
                    </StatusBadge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</p>
                  {entry.details ? (
                    <pre className="mt-2 overflow-x-auto bg-muted p-3 text-[11px] text-muted-foreground">{JSON.stringify(entry.details, null, 2)}</pre>
                  ) : null}
                </div>
              )) : (
                <div className="px-4 py-4 text-sm text-muted-foreground">
                  Noch keine lokalen Support-Ereignisse gespeichert.
                </div>
              )}
            </div>
          </div>
        </DetailCard>

        <DetailCard title="Aktionen">
          <div className="space-y-3">
            <button
              type="button"
              onClick={onDownload}
              className="flex h-11 w-full items-center justify-center border border-border bg-card text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              Support Log herunterladen
            </button>
            <button
              type="button"
              onClick={onUpload}
              disabled={isUploading}
              className="flex h-11 w-full items-center justify-center bg-lime-500 text-sm font-semibold text-white transition-colors hover:bg-lime-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Support Log hochladen
            </button>
            <button
              type="button"
              onClick={onClear}
              className="flex h-11 w-full items-center justify-center border border-border bg-card text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
            >
              Lokale Einträge löschen
            </button>
          </div>
        </DetailCard>
      </div>
    </div>
  );
}

export default function PosSettings() {
  const { user, logout } = useAuth();
  const {
    syncData,
    isSyncing,
    isOnline,
    pendingOrdersCount,
    lastSyncTime,
    demoModeEnabled,
    setDemoModeEnabled,
  } = useOffline();
  const { setSubtitle } = usePosSubtitle();
  const [activeSection, setActiveSection] = useState('fiskalisierung');
  const [payload, setPayload] = useState(null);
  const [devicesData, setDevicesData] = useState(null);
  const [devicesUnavailable, setDevicesUnavailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [testingPrinterId, setTestingPrinterId] = useState('');
  const [isUploadingSupportLog, setIsUploadingSupportLog] = useState(false);
  const [isPrintingNullbeleg, setIsPrintingNullbeleg] = useState(false);
  const [isResyncingFiscal, setIsResyncingFiscal] = useState(false);
  const [selectedPrinterId, setSelectedPrinterId] = useState(null);
  const [supportEntries, setSupportEntries] = useState(readSupportLogEntries());
  const [uiPreferences, setUiPreferences] = useState(readPosUiPreferences());
  const [language, setLanguage] = useState(i18n.resolvedLanguage || i18n.language || 'de');
  const [lastDeviceRefreshAt, setLastDeviceRefreshAt] = useState(null);
  const [sessionId] = useState(() => `pos_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const settings = payload?.settings || EMPTY_OBJECT;
  const printers = payload?.printers || EMPTY_ARRAY;
  const printGroups = payload?.print_groups || EMPTY_ARRAY;
  const rooms = payload?.rooms || EMPTY_ARRAY;
  const fiscalStatus = payload?.fiscal_status || EMPTY_OBJECT;
  const cashRegister = payload?.cash_register || null;
  const posSettingsConfig = useMemo(
    () => normalizePosSettingsConfig(payload?.pos_settings_config || DEFAULT_POS_SETTINGS_CONFIG),
    [payload?.pos_settings_config],
  );

  const companyName = settings?.company_name || user?.company_name || 'FiscalPOS';
  const isManager = ['admin', 'manager', 'superadmin'].includes(String(user?.role || '').toLowerCase());
  const devices = devicesData?.devices || EMPTY_ARRAY;
  const devicesById = useMemo(
    () => Object.fromEntries(devices.map((device) => [device.id, device])),
    [devices],
  );
  // Printers are always shown with the device they hang on, e.g. "Bondrucker · Bar-PC".
  const printerOptions = useMemo(
    () => printers.map((printer) => ({
      value: printer.id,
      label: `${printer.name} · ${devicesById[printer.device_id]?.name || 'ohne Gerät'}`,
    })),
    [printers, devicesById],
  );

  const logSupport = useCallback((entry) => {
    const nextEntries = appendSupportLogEntry(entry);
    setSupportEntries(nextEntries);
  }, []);

  const refreshSupportEntries = useCallback(() => {
    setSupportEntries(readSupportLogEntries());
  }, []);

  const deviceInfo = useMemo(() => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
      return {
        ipAddress: '127.0.0.1',
        vendor: 'Browser',
        model: 'Unbekannt',
        os: 'Web',
        platformVersion: 'Aktuelle Version',
        serviceWorkerSupported: false,
        isStandalone: false,
      };
    }

    const userAgent = navigator.userAgent || '';
    const hostname = window.location.hostname || 'localhost';
    const os = /Android/i.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|iPod/i.test(userAgent)
        ? 'iOS'
        : /Mac OS X/i.test(userAgent)
          ? 'macOS'
          : /Windows/i.test(userAgent)
            ? 'Windows'
            : 'Web';
    const isStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone || false;

    return {
      ipAddress: hostname === 'localhost' ? '127.0.0.1' : hostname,
      vendor: /SUNMI/i.test(userAgent) ? 'SUNMI' : 'Browser',
      model: userAgent.split(')')[0]?.split('(')?.[1] || 'PWA Client',
      os,
      platformVersion: navigator.platform || 'Aktuelle Version',
      serviceWorkerSupported: 'serviceWorker' in navigator,
      isStandalone,
    };
  }, []);

  const supportSnapshot = useMemo(() => ({
    session_id: sessionId,
    generated_at: new Date().toISOString(),
    user: {
      id: user?.id || '',
      name: user?.name || '',
      role: user?.role || '',
      company_id: user?.company_id || '',
    },
    device: deviceInfo,
    settings: {
      company_name: settings.company_name || '',
      terminal_id: settings.terminal_id || '',
      kassen_id: settings.kassen_id || '',
      currency: settings.currency || 'EUR',
      currency_symbol: settings.currency_symbol || '€',
    },
    fiscal_status: fiscalStatus,
    cash_register: cashRegister,
    pos_settings_config: posSettingsConfig,
    printers: printers.map((printer) => ({
      id: printer.id,
      name: printer.name,
      type: printer.type,
      connection_string: printer.connection_string,
      is_active: printer.is_active,
    })),
    print_groups: printGroups.map((group) => ({
      id: group.id,
      name: group.name,
      printer_id: group.printer_id || null,
    })),
    rooms: rooms.map((room) => ({
      id: room.id,
      name: room.name,
      printer_id: posSettingsConfig.room_printers?.[room.id] || null,
    })),
    language,
    ui_preferences: uiPreferences,
    is_online: isOnline,
    is_syncing: isSyncing,
    demo_mode_enabled: demoModeEnabled,
    pending_orders_count: pendingOrdersCount,
    last_sync_at: lastSyncTime instanceof Date ? lastSyncTime.toISOString() : lastSyncTime || null,
  }), [
    sessionId,
    user,
    deviceInfo,
    settings,
    fiscalStatus,
    cashRegister,
    posSettingsConfig,
    printers,
    printGroups,
    rooms,
    language,
    uiPreferences,
    isOnline,
    isSyncing,
    demoModeEnabled,
    pendingOrdersCount,
    lastSyncTime,
  ]);

  const statusItems = useMemo(() => {
    const fiscalReady = Boolean(fiscalStatus?.tse_ok || fiscalStatus?.mock_mode || settings?.ft_is_mock);
    const printerReady = printers.some((printer) => printer.is_active);
    const syncPending = pendingOrdersCount === 0;

    return [
      {
        label: 'Netzwerk',
        ok: isOnline,
        value: isOnline ? 'Verbindung zur Cloud und lokalen Diensten aktiv.' : 'Aktuell offline. Bitte Verbindung prüfen.',
      },
      {
        label: 'Cloud',
        ok: isOnline,
        value: isOnline ? 'Backend erreichbar und antwortet für diese POS-Sitzung.' : 'Keine Cloud-Verbindung verfügbar.',
      },
      {
        label: 'Sync ausstehend',
        ok: syncPending && !demoModeEnabled,
        tone: demoModeEnabled ? 'info' : 'warning',
        badge: demoModeEnabled ? 'Demo' : 'Prüfen',
        value: demoModeEnabled
          ? `${pendingOrdersCount} Vorgänge bleiben lokal, weil Demo-Modus aktiv ist.`
          : syncPending
            ? 'Keine ausstehenden Vorgänge.'
            : `${pendingOrdersCount} Vorgänge warten auf Übertragung.`,
      },
      {
        label: 'Tag / Bediener',
        ok: Boolean(user?.name),
        value: user?.name ? `Angemeldet als ${user.name}.` : 'Kein Benutzer angemeldet.',
      },
      {
        label: 'Fiskalisierung',
        ok: fiscalReady,
        tone: settings?.ft_is_mock ? 'warning' : 'danger',
        badge: settings?.ft_is_mock ? 'Mock' : 'Prüfen',
        value: fiscalStatus?.tse_ok
          ? 'fiskaltrust ist produktiv verbunden und kann signieren.'
          : settings?.ft_is_mock
            ? 'Mock-Modus aktiv. Testbelege werden erzeugt.'
            : 'Konfiguration unvollständig oder noch nicht geprüft.',
      },
      {
        label: 'Dienste',
        ok: printerReady || isOnline,
        value: printerReady
          ? `${printers.length} Drucker erkannt, Basisdienste aktiv.`
          : 'Basisdienste aktiv, Drucker noch nicht eingerichtet.',
      },
    ];
  }, [demoModeEnabled, fiscalStatus, isOnline, pendingOrdersCount, printers, settings?.ft_is_mock, user?.name]);

  const serviceItems = useMemo(() => [
    {
      label: 'Datensynchronisation',
      description: demoModeEnabled
        ? 'Durch Demo-Modus pausiert. Vorgänge bleiben lokal gespeichert.'
        : isOnline
          ? 'Synchronisiert Daten mit der Cloud.'
          : 'Der Dienst wartet auf eine Online-Verbindung.',
      active: isOnline && !demoModeEnabled,
      tone: demoModeEnabled ? 'info' : 'warning',
    },
    {
      label: 'Lokale Warteschlange',
      description: pendingOrdersCount > 0
        ? `${pendingOrdersCount} lokale Vorgänge warten auf Übertragung.`
        : 'Keine lokalen Vorgänge in der Warteschlange.',
      active: true,
      tone: pendingOrdersCount > 0 ? 'warning' : 'positive',
    },
    {
      label: 'Druckdienst',
      description: printers.length > 0
        ? `${printers.length} Drucker stehen dieser Kassa zur Verfügung.`
        : 'Noch keine Drucker für diese Firma konfiguriert.',
      active: printers.some((printer) => printer.is_active),
    },
    {
      label: 'Fiskalservice',
      description: fiscalStatus?.tse_ok
        ? 'fiskaltrust antwortet produktiv.'
        : settings?.ft_is_mock
          ? 'Mock-Modus aktiv, Produktivsignatur ist aus.'
          : 'Produktivsignatur derzeit nicht bestätigt.',
      active: Boolean(fiscalStatus?.tse_ok || settings?.ft_is_mock),
      tone: settings?.ft_is_mock ? 'warning' : 'danger',
    },
    {
      label: 'Service Worker',
      description: deviceInfo.serviceWorkerSupported
        ? 'Offline-Caching und Hintergrundfunktionen sind verfügbar.'
        : 'Dieses Gerät bietet keinen Service Worker für die POS-PWA.',
      active: deviceInfo.serviceWorkerSupported,
    },
    {
      label: 'Support Upload',
      description: 'Diagnosepakete können direkt im Backend abgelegt werden.',
      active: true,
    },
  ], [demoModeEnabled, deviceInfo.serviceWorkerSupported, fiscalStatus?.tse_ok, isOnline, pendingOrdersCount, printers, settings?.ft_is_mock]);

  useEffect(() => {
    setSubtitle(SECTION_SUBTITLES[activeSection] || '');
    return () => setSubtitle('');
  }, [activeSection, setSubtitle]);

  useEffect(() => {
    applyPosUiPreferences(uiPreferences);
  }, [uiPreferences]);

  useEffect(() => {
    if (printers.length === 0) {
      setSelectedPrinterId(null);
      return;
    }

    setSelectedPrinterId((current) => (
      printers.some((printer) => printer.id === current) ? current : printers[0].id
    ));
  }, [printers]);

  const loadPayload = useCallback(async ({ silent = false, logMessage = 'POS-Settings geladen.' } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    setIsRefreshing(true);

    try {
      const [data, devicesResult] = await Promise.all([
        getPosSettingsPayload(),
        getDevices().then(
          (result) => ({ ok: true, result }),
          (error) => ({ ok: false, error }),
        ),
      ]);
      setPayload(data);
      setDevicesData(devicesResult.ok ? devicesResult.result : null);
      setDevicesUnavailable(!devicesResult.ok);
      setLastDeviceRefreshAt(new Date());
      logSupport({
        type: 'settings',
        message: logMessage,
        details: {
          printers: data?.printers?.length || 0,
          print_groups: data?.print_groups?.length || 0,
          rooms: data?.rooms?.length || 0,
        },
      });
    } catch (error) {
      console.error('Error loading POS settings payload:', error);
      logSupport({
        type: 'settings',
        level: 'error',
        message: 'POS-Settings konnten nicht geladen werden.',
        details: {
          status: error?.response?.status || null,
          error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler',
        },
      });
      toast.error(error?.response?.data?.detail || 'POS-Einstellungen konnten nicht geladen werden');
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, [logSupport]);

  useEffect(() => {
    loadPayload({ logMessage: 'POS-Settings initial geladen.' });
  }, [loadPayload]);

  const updateConfig = async (updater, saveKey, successMessage, logEntry) => {
    const previousConfig = normalizePosSettingsConfig(posSettingsConfig);
    const nextConfig = normalizePosSettingsConfig(
      typeof updater === 'function' ? updater(previousConfig) : updater,
    );

    setSavingKey(saveKey);
    setPayload((current) => current ? { ...current, pos_settings_config: nextConfig } : current);

    try {
      const response = await savePosSettingsConfig(nextConfig);
      setPayload((current) => current ? { ...current, pos_settings_config: response.pos_settings_config } : current);
      toast.success(successMessage);
      logSupport(logEntry);
    } catch (error) {
      setPayload((current) => current ? { ...current, pos_settings_config: previousConfig } : current);
      toast.error(error?.response?.data?.detail || 'Änderung konnte nicht gespeichert werden');
      logSupport({
        type: 'settings',
        level: 'error',
        message: 'POS-Konfiguration konnte nicht gespeichert werden.',
        details: { save_key: saveKey, error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setSavingKey('');
    }
  };

  const handleAssignSystemPrinter = async (groupId, printerId) => {
    await updateConfig(
      (current) => ({
        ...current,
        system_printers: {
          ...current.system_printers,
          [groupId]: printerId || null,
        },
      }),
      `system:${groupId}`,
      'Systemdrucker gespeichert',
      {
        type: 'printer_assignment',
        message: `Systemgruppe ${groupId} neu zugewiesen.`,
        details: { group_id: groupId, printer_id: printerId || null },
      },
    );
  };

  const handleAssignRoomPrinter = async (roomId, printerId) => {
    await updateConfig(
      (current) => ({
        ...current,
        room_printers: {
          ...current.room_printers,
          [roomId]: printerId || null,
        },
      }),
      `room:${roomId}`,
      'Raumregel gespeichert',
      {
        type: 'printer_assignment',
        message: `Raum ${roomId} neu zugewiesen.`,
        details: { room_id: roomId, printer_id: printerId || null },
      },
    );
  };

  const handleAssignGroupPrinter = async (groupId, printerId) => {
    const previousGroups = printGroups;
    setSavingKey(`group:${groupId}`);
    setPayload((current) => current ? {
      ...current,
      print_groups: (current.print_groups || []).map((group) => (
        group.id === groupId ? { ...group, printer_id: printerId || null } : group
      )),
    } : current);

    try {
      const updatedGroup = await updatePosPrintGroup(groupId, { printer_id: printerId || null });
      setPayload((current) => current ? {
        ...current,
        print_groups: (current.print_groups || []).map((group) => (
          group.id === groupId ? updatedGroup : group
        )),
      } : current);
      toast.success('Druckgruppe gespeichert');
      logSupport({
        type: 'printer_assignment',
        message: `Druckgruppe ${groupId} neu zugewiesen.`,
        details: { group_id: groupId, printer_id: printerId || null },
      });
    } catch (error) {
      setPayload((current) => current ? { ...current, print_groups: previousGroups } : current);
      toast.error(error?.response?.data?.detail || 'Druckgruppe konnte nicht gespeichert werden');
      logSupport({
        type: 'printer_assignment',
        level: 'error',
        message: 'Druckgruppe konnte nicht gespeichert werden.',
        details: { group_id: groupId, error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setSavingKey('');
    }
  };

  const handleRefresh = async () => {
    await loadPayload({ silent: true, logMessage: 'POS-Settings manuell aktualisiert.' });
    toast.success('Status aktualisiert');
  };

  const handleTestPrinter = async (printer) => {
    if (!printer?.id) return;
    setTestingPrinterId(printer.id);
    try {
      const result = await testPosPrinter(printer.id);
      if (result?.job_id) {
        const job = await waitForPrintJob(result.job_id);
        if (job?.status === 'failed') throw new Error(job.error || 'Druck fehlgeschlagen');
        if (job?.status !== 'done') {
          toast.warning(`Noch keine Rückmeldung von ${result.device_name || 'dem Gerät'}. Ist dort die Kasse geöffnet?`);
          return;
        }
      }
      toast.success(result?.job_id ? `Testseite auf ${printer.name} gedruckt` : result?.message || `Testseite an ${printer.name} gesendet`);
      logSupport({
        type: 'printer_test',
        message: `Testseite an ${printer.name} gesendet.`,
        details: { printer_id: printer.id },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Testdruck fehlgeschlagen');
      logSupport({
        type: 'printer_test',
        level: 'error',
        message: `Testdruck für ${printer.name} fehlgeschlagen.`,
        details: { printer_id: printer.id, error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setTestingPrinterId('');
    }
  };

  const handlePrintNullbeleg = async () => {
    setIsPrintingNullbeleg(true);
    try {
      const result = await printPosNullbeleg();
      toast.success(`Null-Beleg ${result?.receipt_number || ''} gedruckt`);
      logSupport({
        type: 'fiscal',
        message: 'Null-Beleg erfolgreich erzeugt.',
        details: {
          receipt_number: result?.receipt_number || null,
          is_mock: Boolean(result?.is_mock),
        },
      });
      await loadPayload({ silent: true, logMessage: 'Fiskalstatus nach Null-Beleg aktualisiert.' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Null-Beleg konnte nicht gedruckt werden');
      logSupport({
        type: 'fiscal',
        level: 'error',
        message: 'Null-Beleg fehlgeschlagen.',
        details: { error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setIsPrintingNullbeleg(false);
    }
  };

  const handleFiscalResync = async () => {
    setIsResyncingFiscal(true);
    try {
      const result = await resyncPosFiscalOrders();
      toast.success(`${result?.resynced || 0} Vorgänge geprüft`);
      logSupport({
        type: 'fiscal',
        message: 'Fiskal-Resync ausgeführt.',
        details: { resynced: result?.resynced || 0 },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Fiskal-Resync fehlgeschlagen');
      logSupport({
        type: 'fiscal',
        level: 'error',
        message: 'Fiskal-Resync fehlgeschlagen.',
        details: { error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setIsResyncingFiscal(false);
    }
  };

  const handleLanguageChange = async (nextLanguage) => {
    try {
      await i18n.changeLanguage(nextLanguage);
      setLanguage(i18n.resolvedLanguage || nextLanguage);
      toast.success('Sprache aktualisiert');
      logSupport({
        type: 'language',
        message: `Sprache auf ${nextLanguage} umgestellt.`,
      });
    } catch (error) {
      toast.error('Sprache konnte nicht geändert werden');
      logSupport({
        type: 'language',
        level: 'error',
        message: 'Sprachwechsel fehlgeschlagen.',
        details: { error: error?.message || 'Unbekannter Fehler' },
      });
    }
  };

  const handleUiPreferenceChange = (key, value) => {
    const nextPreferences = writePosUiPreferences({
      ...uiPreferences,
      [key]: value,
    });
    setUiPreferences(nextPreferences);
    applyPosUiPreferences(nextPreferences);
    toast.success('Darstellung aktualisiert');
    logSupport({
      type: 'ui_preferences',
      message: `Darstellung ${key} geändert.`,
      details: { key, value },
    });
  };

  const handleToggleDemoMode = () => {
    const nextValue = !demoModeEnabled;
    setDemoModeEnabled(nextValue);
    toast.success(nextValue ? 'Demo-Modus aktiviert' : 'Demo-Modus deaktiviert');
  };

  const handleDownloadSupportLog = () => {
    const payloadToDownload = {
      ...supportSnapshot,
      entries: supportEntries,
    };
    downloadJsonFile(`support-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`, payloadToDownload);
    logSupport({
      type: 'support_log',
      message: 'Support-Log lokal heruntergeladen.',
      details: { entries: supportEntries.length },
    });
  };

  const handleUploadSupportLog = async () => {
    setIsUploadingSupportLog(true);
    try {
      const result = await uploadPosSupportLog({
        session_id: supportSnapshot.session_id,
        generated_at: supportSnapshot.generated_at,
        snapshot: supportSnapshot,
        entries: supportEntries,
      });
      toast.success(`Support-Log hochgeladen (${result?.record_id || 'OK'})`);
      logSupport({
        type: 'support_log',
        message: 'Support-Log an Backend hochgeladen.',
        details: { record_id: result?.record_id || null, entries: result?.received_entries || supportEntries.length },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Support-Log konnte nicht hochgeladen werden');
      logSupport({
        type: 'support_log',
        level: 'error',
        message: 'Support-Log Upload fehlgeschlagen.',
        details: { error: error?.response?.data?.detail || error?.message || 'Unbekannter Fehler' },
      });
    } finally {
      setIsUploadingSupportLog(false);
    }
  };

  const handleClearSupportLog = () => {
    clearSupportLogEntries();
    refreshSupportEntries();
    toast.success('Lokales Support-Log gelöscht');
  };

  const handleMenuClick = async (item) => {
    if (item.kind === 'section') {
      setActiveSection(item.id);
      return;
    }

    switch (item.id) {
      case 'sync': {
        try {
          const result = await syncData();
          if (result?.reason === 'demo_mode') {
            toast.info('Demo-Modus aktiv: Synchronisation bleibt deaktiviert');
          } else {
            toast.success('Daten synchronisiert');
          }
        } catch {
          toast.error('Synchronisation fehlgeschlagen');
        }
        break;
      }
      case 'support_log_upload':
        await handleUploadSupportLog();
        break;
      case 'restart':
        logSupport({
          type: 'session',
          message: 'Anwendung manuell neu gestartet.',
        });
        toast.info('Anwendung wird neu gestartet…');
        setTimeout(() => window.location.reload(), 900);
        break;
      case 'logout':
        logSupport({
          type: 'session',
          message: 'Benutzer manuell abgemeldet.',
        });
        logout();
        break;
      default:
        break;
    }
  };

  let bottomAction = null;
  if (activeSection === 'fiskalisierung') {
    bottomAction = {
      label: isPrintingNullbeleg ? 'Wird gedruckt…' : 'Drucken',
      sub: 'NULL-BELEG DIREKT AN FISKALTRUST',
      color: 'bg-lime-500 hover:bg-lime-600',
      onClick: handlePrintNullbeleg,
      disabled: isPrintingNullbeleg,
    };
  } else if (activeSection === 'demo') {
    bottomAction = {
      label: demoModeEnabled ? 'Demo deaktivieren' : 'Demo aktivieren',
      sub: demoModeEnabled ? 'SYNC WIEDER AKTIVIEREN' : 'LOKAL TESTEN',
      color: demoModeEnabled ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-800 hover:bg-slate-900',
      onClick: handleToggleDemoMode,
      disabled: false,
    };
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'fiskalisierung':
        return (
          <FiskalisierungPanel
            companyName={companyName}
            settings={settings}
            fiscalStatus={fiscalStatus}
            statusItems={statusItems}
            cashRegister={cashRegister}
            onRefresh={handleRefresh}
            onResync={handleFiscalResync}
            isRefreshing={isRefreshing}
            isResyncing={isResyncingFiscal}
          />
        );
      case 'geraet':
        return (
          <DeviceSettingsPanel
            isManager={isManager}
            devicesUnavailable={devicesUnavailable}
            printerOptions={printerOptions}
            onChanged={() => loadPayload({ silent: true, logMessage: 'Geräte/Drucker aktualisiert.' })}
          />
        );
      case 'druckerzuweisung':
        return (
          <DruckerzuweisungPanel
            printerOptions={printerOptions}
            printGroups={printGroups}
            rooms={rooms}
            systemAssignments={posSettingsConfig.system_printers}
            roomAssignments={posSettingsConfig.room_printers}
            savingKey={savingKey}
            onAssignSystemPrinter={handleAssignSystemPrinter}
            onAssignGroupPrinter={handleAssignGroupPrinter}
            onAssignRoomPrinter={handleAssignRoomPrinter}
          />
        );
      case 'druckerstatus':
        return (
          <DruckerstatusPanel
            printers={printers}
            devicesById={devicesById}
            printGroups={printGroups}
            selectedPrinterId={selectedPrinterId}
            onSelectPrinter={setSelectedPrinterId}
            onRefresh={handleRefresh}
            onTestPrinter={handleTestPrinter}
            isRefreshing={isRefreshing}
            testingPrinterId={testingPrinterId}
            lastDeviceRefreshAt={lastDeviceRefreshAt}
          />
        );
      case 'nebenterminals':
        return (
          <NebenterminalsPanel
            deviceInfo={deviceInfo}
            devices={devices}
            settings={settings}
            cashRegister={cashRegister}
            lastSyncTime={lastSyncTime}
            pendingOrdersCount={pendingOrdersCount}
            demoModeEnabled={demoModeEnabled}
          />
        );
      case 'dienste':
        return <DienstePanel statusItems={statusItems} serviceItems={serviceItems} companyName={companyName} />;
      case 'sprache':
        return <SprachePanel language={language} settings={settings} onChangeLanguage={handleLanguageChange} />;
      case 'darstellung':
        return <DarstellungPanel uiPreferences={uiPreferences} onUpdatePreference={handleUiPreferenceChange} />;
      case 'demo':
        return <DemoPanel demoEnabled={demoModeEnabled} onToggle={handleToggleDemoMode} pendingOrdersCount={pendingOrdersCount} />;
      case 'hilfe':
        return (
          <HilfePanel
            onSyncNow={() => handleMenuClick({ id: 'sync', kind: 'action' })}
            onOpenSupportLog={() => setActiveSection('support_log')}
            onOpenPrinterStatus={() => setActiveSection('druckerstatus')}
            onDownloadSupportLog={handleDownloadSupportLog}
            isSyncing={isSyncing}
          />
        );
      case 'support_log':
        return (
          <SupportLogPanel
            entries={supportEntries}
            snapshot={supportSnapshot}
            isUploading={isUploadingSupportLog}
            onDownload={handleDownloadSupportLog}
            onUpload={handleUploadSupportLog}
            onClear={handleClearSupportLog}
          />
        );
      default:
        return (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            Abschnitt nicht verfügbar
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-muted" data-testid="pos-settings">
      <aside className="hidden w-72 flex-shrink-0 border-r border-border bg-card lg:flex lg:flex-col">
        <div className="border-b border-border px-5 py-5">
          <p className="text-2xl font-bold text-foreground">Einstellungen</p>
          <p className="mt-1 text-sm text-muted-foreground">{settings?.kassen_id || 'KASSA 01'} · {user?.name || 'Kellner'}</p>
          <div className="mt-4 flex items-center gap-2">
            {isOnline ? <Wifi className="h-4 w-4 text-lime-600" /> : <WifiOff className="h-4 w-4 text-amber-600" />}
            <span className="text-sm font-medium text-muted-foreground">
              {demoModeEnabled ? 'Demo aktiv' : isOnline ? 'Online' : 'Offline'}
            </span>
            {isSyncing ? <RefreshCw className="ml-auto h-4 w-4 animate-spin text-muted-foreground" /> : null}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.kind === 'section' && activeSection === item.id;
            const toneClass =
              item.tone === 'danger'
                ? 'text-red-600'
                : item.tone === 'warning'
                  ? 'text-amber-700'
                  : 'text-muted-foreground';

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMenuClick(item)}
                className={`flex w-full items-center gap-3 px-5 py-3 text-left text-base transition-colors ${
                  isActive ? 'bg-muted font-semibold text-foreground' : `${toneClass} hover:bg-muted`
                }`}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-foreground' : item.tone === 'danger' ? 'text-red-500' : item.tone === 'warning' ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <span>{item.label}</span>
                {item.id === 'sync' && isSyncing ? <RefreshCw className="ml-auto h-4 w-4 animate-spin text-muted-foreground" /> : null}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-border px-5 py-4">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Version</p>
          <p className="mt-1 text-sm text-muted-foreground">FiscalPOS PWA</p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="border-b border-border bg-card px-3 py-3 lg:hidden">
          <div className="flex gap-2 overflow-x-auto">
            {MENU_ITEMS.map((item) => {
              const isActive = item.kind === 'section' && activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleMenuClick(item)}
                  className={`whitespace-nowrap px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : item.kind === 'action'
                        ? 'border border-border bg-card text-muted-foreground'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {renderContent()}

        {bottomAction ? (
          <button
            type="button"
            onClick={bottomAction.onClick}
            disabled={bottomAction.disabled}
            className={`flex-shrink-0 px-5 py-4 text-white transition-colors ${bottomAction.color} disabled:cursor-not-allowed disabled:opacity-70`}
          >
            <span className="block text-2xl font-bold">{bottomAction.label}</span>
            <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.14em] text-white/80">
              {bottomAction.sub}
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
